import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSEO } from '../hooks/useSEO';
import { GUIDE_TO_HPS_BY_LANG } from '../content/guideToHpsContent';

/**
 * Guide to HPS — wording is an exact copy of docs/Guide to HPS.md.
 * Layout/styling only (HPS colors, cards, lists, tables, mobile).
 */

const SEPARATOR = /^_{10,}$/;

function resolveGuideLang(lng) {
  const base = String(lng || 'en').split('-')[0].toLowerCase();
  return GUIDE_TO_HPS_BY_LANG[base] ? base : 'en';
}

function isCalendarSectionTitle(title) {
  return /league calendar|calendrier de la ligue|ligakalender|calendario de la liga|calendario della lega/i.test(
    title
  );
}

function isGlanceSectionTitle(title) {
  return /league at a glance|aperçu de la ligue|liga im überblick|panoramica del campionato|la liga en resumen/i.test(
    title
  );
}

function isPhilosophySectionTitle(title) {
  return /league philosophy|philosophie de la ligue|ligaphilosophie|filosofia della lega|filosofía de la liga/i.test(
    title
  );
}

function isFinalNotesTitle(title) {
  return /final notes|notes finales|schlussbemerkungen|note conclusive|notas finales/i.test(title);
}

function isImportantLine(line) {
  return /^(Important|Wichtig|Importante)\s*:/i.test(String(line || '').trim());
}

function isTaglineEmphasis(line) {
  const t = String(line || '').trim();
  if (!t || t.length > 140) return false;
  if (!/^(Play|Jouez|Spiele|Gioca|Juega)\b/i.test(t)) return false;
  const segments = t.split(/\.\s+/).filter(Boolean);
  return segments.length >= 3 && t.endsWith('.');
}

function isMotivatedTeamEmphasis(line) {
  const t = String(line || '').trim();
  if (!t) return false;
  return (
    /motivated team|motiviertes team|équipe motivée|squadra motivata|equipo motivado/i.test(t) &&
    /(league match|ligaspiel|matchs de championnat|partite di campionato|partidos de liga)/i.test(t)
  );
}

function buildParseContext(raw) {
  const emphasisLines = new Set();
  let closingLine = null;
  for (const line of String(raw || '').split('\n')) {
    const t = line.trim();
    if (!t) continue;
    if (isTaglineEmphasis(t)) {
      emphasisLines.add(t);
      closingLine = t;
    }
    if (isMotivatedTeamEmphasis(t)) emphasisLines.add(t);
  }
  return { emphasisLines, closingLine };
}

function getBlockKind(title) {
  if (isCalendarSectionTitle(title)) return 'calendar';
  if (isGlanceSectionTitle(title)) return 'glance';
  return 'default';
}

const PageContainer = styled.div`
  min-height: ${(p) => (p.$embedded ? 'auto' : '100vh')};
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding-top: ${(p) => (p.$embedded ? '0' : '90px')};
  padding-bottom: 3rem;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 20px 2rem;

  @media (max-width: 480px) {
    padding: 0 12px 1.5rem;
  }
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 3.5rem 1.25rem 3rem;
  background: linear-gradient(135deg, var(--brand-blue) 0%, var(--brand-blue-light, #0a4d7a) 100%);
  color: white;
  margin-bottom: 2rem;
  border-radius: 0 0 24px 24px;

  @media (max-width: 768px) {
    padding: 2.5rem 1rem 2.25rem;
    margin-bottom: 1.5rem;
  }
`;

const HeroTitle = styled.h1`
  font-size: 2.75rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ContentSection = styled.section`
  background: white;
  border-radius: 16px;
  padding: 2.5rem 2.75rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 1.5rem 1.15rem;
    border-radius: 12px;
    margin-bottom: 1.15rem;
  }
`;

const SectionTitle = styled.h2`
  color: var(--brand-blue);
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 1.35rem;
  border-bottom: 3px solid var(--brand-red);
  padding-bottom: 0.45rem;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    font-size: 1.35rem;
  }
`;

const NumberBadge = styled.span`
  background: var(--brand-red);
  color: white;
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    min-width: 28px;
    font-size: 0.9rem;
  }
`;

const SubsectionTitle = styled.h3`
  color: var(--brand-blue);
  font-size: 1.25rem;
  font-weight: 650;
  margin: 1.75rem 0 0.75rem;
  line-height: 1.35;

  &:first-of-type {
    margin-top: 0;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Paragraph = styled.p`
  margin: 0 0 0.9rem;
  color: #334155;
  font-size: 1.02rem;
  line-height: 1.7;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EmphasisLine = styled.p`
  margin: 1.15rem 0;
  color: var(--brand-blue);
  font-size: 1.1rem;
  font-weight: 650;
  line-height: 1.55;
  text-align: center;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 1rem;
`;

const BulletItem = styled.li`
  position: relative;
  padding-left: 1.75rem;
  margin-bottom: 0.65rem;
  line-height: 1.6;
  color: #334155;
  font-size: 1.02rem;

  &::before {
    content: '•';
    color: var(--brand-red);
    font-weight: bold;
    position: absolute;
    left: 0;
    font-size: 1.25rem;
    line-height: 1.35;
  }
`;

const NumberList = styled.ol`
  list-style: none;
  margin: 0.75rem 0 1.15rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const NumberItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.85rem;
  align-items: start;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 4px solid var(--brand-blue);
  border-radius: 10px;
  padding: 1rem 1.1rem;

  .num-badge {
    background: var(--brand-blue);
    color: white;
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.9rem;
    margin-top: 0.1rem;
  }

  .num-body {
    min-width: 0;
  }

  .num-title {
    display: block;
    font-weight: 700;
    color: var(--brand-blue);
    font-size: 1.05rem;
    margin-bottom: 0.25rem;
    line-height: 1.35;
  }

  .num-text {
    margin: 0;
    color: #334155;
    font-size: 0.98rem;
    line-height: 1.55;
  }
`;

const InfoNote = styled.div`
  margin-top: 1.25rem;
  padding: 0.95rem 1.1rem;
  background: #eff6ff;
  border-left: 4px solid var(--brand-blue);
  border-radius: 8px;
  color: #1e3a5f;
  font-size: 0.98rem;
  line-height: 1.55;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin: 0.35rem 0 0.5rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const CalendarCard = styled.div`
  background: linear-gradient(135deg, #e8eef5 0%, #dbe7f3 100%);
  border: 2px solid var(--brand-blue);
  border-radius: 12px;
  padding: 1.25rem 1.1rem;
  text-align: center;

  h4 {
    margin: 0 0 0.55rem;
    color: var(--brand-blue);
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.3;
  }

  .dates {
    margin: 0;
    color: #0f172a;
    font-weight: 600;
    font-size: 0.98rem;
    line-height: 1.4;
  }

  .extra {
    margin: 0.35rem 0 0;
    color: #475569;
    font-size: 0.88rem;
  }
`;

const GlanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 0.85rem;
  margin: 0.75rem 0 0.25rem;
`;

const GlanceCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 4px solid var(--brand-red);
  border-radius: 10px;
  padding: 1rem 1.05rem;

  .label {
    display: block;
    color: var(--brand-blue);
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    margin-bottom: 0.35rem;
  }

  .value {
    margin: 0;
    color: #1e293b;
    font-size: 0.98rem;
    line-height: 1.5;
    font-weight: 500;
    white-space: pre-line;
  }
`;

const PrincipleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const PrincipleCard = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%);
  border: 1px solid #e2e8f0;
  border-top: 4px solid var(--brand-blue);
  border-radius: 12px;
  padding: 1.25rem 1.15rem;

  h4 {
    margin: 0 0 0.65rem;
    color: var(--brand-blue);
    font-size: 1.1rem;
    font-weight: 700;
  }

  p {
    margin: 0 0 0.55rem;
    color: #334155;
    font-size: 0.98rem;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0.85rem 0 1.1rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 280px;
  font-size: 0.95rem;

  th {
    background: var(--brand-blue);
    color: white;
    text-align: left;
    padding: 0.7rem 0.85rem;
    font-weight: 650;
  }

  td {
    padding: 0.65rem 0.85rem;
    border-top: 1px solid #e2e8f0;
    color: #334155;
    vertical-align: top;
  }

  tr:nth-child(even) td {
    background: #f8fafc;
  }
`;

const ClosingBanner = styled.div`
  margin-top: 1.5rem;
  padding: 1.35rem 1.35rem;
  background: linear-gradient(135deg, var(--brand-blue) 0%, var(--brand-blue-light, #0a4d7a) 100%);
  color: white;
  border-radius: 12px;
  text-align: center;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.45;

  @media (max-width: 480px) {
    font-size: 1.05rem;
    padding: 1.15rem 1rem;
  }
`;

function isBullet(line) {
  return /^[•·]/.test(line);
}

function bulletText(line) {
  return line.replace(/^[•·]\s*/, '').replace(/^\t/, '').trim();
}

/** Numbered list item: "1.\\tTitle" (tab), not major section "1. About…" (space). */
function isNumberedListItem(line) {
  return /^\d+\.\t/.test(line);
}

function isMajorSectionTitle(line) {
  return /^\d+\.\s+\S/.test(line);
}

function numberedParts(line) {
  const m = line.match(/^(\d+)\.\t?(.*)$/);
  if (!m) return { n: '', text: line };
  return { n: m[1], text: (m[2] || '').trim() };
}

function isTabRow(line) {
  if (!line.includes('\t')) return false;
  // Bullets / numbered steps in the MD use "•\t…" / "1.\t…" — those are lists, not tables
  if (isBullet(line)) return false;
  if (isNumberedListItem(line)) return false;
  return line.split('\t').filter((c) => c.trim()).length >= 2;
}

function splitTab(line) {
  return line.split('\t').map((c) => c.trim()).filter(Boolean);
}

const MONTH_RE =
  /\b(January|February|March|April|May|June|July|August|September|October|November|December|janvier|février|fevrier|mars|avril|mai|juin|juillet|août|aout|septembre|octobre|novembre|décembre|decembre|Januar|Februar|März|Marz|April|Mai|Juni|Juli|August|September|Oktober|November|Dezember|gennaio|febbraio|marzo|aprile|maggio|giugno|luglio|agosto|settembre|ottobre|novembre|dicembre|enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\b/i;

function isDateLikeLine(line) {
  if (!line) return false;
  if (MONTH_RE.test(line)) return true;
  if (
    /date to be announced|date à annoncer|datum wird noch bekannt|fecha por anunciar|data da annunciare/i.test(
      line
    )
  ) {
    return true;
  }
  if (/^\d{1,2}[\s./-]/.test(line.trim())) return true;
  if (/^\([^)]+\)$/.test(line.trim())) return true;
  return false;
}

function looksLikeSubheading(line, nextLine, ctx) {
  if (!line || line.length > 90) return false;
  if (isBullet(line) || isNumberedListItem(line) || isTabRow(line)) return false;
  if (isMajorSectionTitle(line)) return true;
  if (isImportantLine(line)) return false;
  if (ctx.emphasisLines.has(line)) return false;
  if (isDateLikeLine(line)) return false;
  // Prose / colon-ended lead-ins are not headings
  if (/[.!?:]$/.test(line)) return false;
  if (line.includes(',')) return false;
  if (line.split(/\s+/).length > 10) return false;
  if (!/^[A-ZÀ-ÖØ-Þ0-9]/.test(line) && line.split(/\s+/).length > 4) return false;
  // Prefer when followed by content / list / table / date
  if (
    nextLine === undefined ||
    isBullet(nextLine) ||
    isNumberedListItem(nextLine) ||
    isTabRow(nextLine) ||
    isDateLikeLine(nextLine) ||
    (nextLine && nextLine.length > 20)
  ) {
    return true;
  }
  // Blank next: only treat short title-like lines as headings
  if (nextLine === '' && line.split(/\s+/).length <= 6) return true;
  return false;
}

function pushTextNode(nodes, text, ctx) {
  const t = text.trim();
  if (!t) return;
  if (ctx.emphasisLines.has(t)) nodes.push({ type: 'emphasis', text: t });
  else if (isImportantLine(t)) nodes.push({ type: 'note', text: t });
  else nodes.push({ type: 'p', text: t });
}

function parseBodyLines(rest, { blockKind, ctx }) {
  const nodes = [];
  let i = 0;

  if (blockKind === 'calendar') {
    const cards = [];
    while (i < rest.length) {
      while (i < rest.length && rest[i] === '') i += 1;
      if (i >= rest.length) break;
      if (isImportantLine(rest[i])) {
        nodes.push({ type: 'note', text: rest[i] });
        i += 1;
        continue;
      }
      const label = rest[i];
      i += 1;
      const dates = [];
      while (i < rest.length && rest[i] !== '' && !isImportantLine(rest[i])) {
        // New label once we already have date lines and current line is not date-like
        if (dates.length > 0 && !isDateLikeLine(rest[i])) break;
        dates.push(rest[i]);
        i += 1;
      }
      cards.push({ label, dates });
    }
    if (cards.length) nodes.unshift({ type: 'calendar', cards });
    return nodes;
  }

  if (blockKind === 'glance') {
    while (i < rest.length) {
      const line = rest[i];
      if (line === '' || line === '\t') {
        i += 1;
        continue;
      }
      if (isTabRow(line)) {
        const cols = splitTab(line);
        const extras = [];
        i += 1;
        while (
          i < rest.length &&
          rest[i] !== '' &&
          !isTabRow(rest[i]) &&
          !(
            looksLikeSubheading(rest[i], rest[i + 1], ctx) &&
            rest[i].split(/\s+/).length <= 4 &&
            !rest[i].includes(',')
          )
        ) {
          extras.push(rest[i]);
          i += 1;
        }
        nodes.push({
          type: 'glanceItem',
          label: cols[0],
          value: [cols.slice(1).join(' '), ...extras].filter(Boolean).join('\n'),
        });
        continue;
      }
      if (looksLikeSubheading(line, rest[i + 1], ctx)) {
        nodes.push({ type: 'h3', text: line });
        i += 1;
        continue;
      }
      pushTextNode(nodes, line, ctx);
      i += 1;
    }
    return nodes;
  }

  while (i < rest.length) {
    const line = rest[i];
    if (line === '') {
      i += 1;
      continue;
    }

    if (isTabRow(line)) {
      const rows = [];
      while (i < rest.length && isTabRow(rest[i])) {
        rows.push(splitTab(rest[i]));
        i += 1;
      }
      nodes.push({ type: 'table', rows });
      continue;
    }

    if (isBullet(line)) {
      const items = [];
      while (i < rest.length && isBullet(rest[i])) {
        items.push(bulletText(rest[i]));
        i += 1;
      }
      nodes.push({ type: 'ul', items });
      continue;
    }

    if (isNumberedListItem(line)) {
      const items = [];
      while (i < rest.length && isNumberedListItem(rest[i])) {
        const { n, text } = numberedParts(rest[i]);
        i += 1;
        const descParts = [];
        while (
          i < rest.length &&
          rest[i] !== '' &&
          !isBullet(rest[i]) &&
          !isNumberedListItem(rest[i]) &&
          !isTabRow(rest[i]) &&
          !looksLikeSubheading(rest[i], rest[i + 1], ctx)
        ) {
          descParts.push(rest[i]);
          i += 1;
        }
        items.push({ n, title: text, body: descParts.join(' ').trim() });
      }
      nodes.push({ type: 'ol', items });
      continue;
    }

    if (looksLikeSubheading(line, rest[i + 1], ctx)) {
      nodes.push({ type: 'h3', text: line });
      i += 1;
      continue;
    }

    const parts = [line];
    i += 1;
    while (
      i < rest.length &&
      rest[i] !== '' &&
      !isBullet(rest[i]) &&
      !isNumberedListItem(rest[i]) &&
      !isTabRow(rest[i]) &&
      !ctx.emphasisLines.has(rest[i]) &&
      !looksLikeSubheading(rest[i], rest[i + 1], ctx)
    ) {
      parts.push(rest[i]);
      i += 1;
    }
    pushTextNode(nodes, parts.join(' '), ctx);
  }

  return nodes;
}

function parseBlock(rawBlock, ctx) {
  const lines = String(rawBlock)
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((l) => l.replace(/\s+$/, ''));

  while (lines.length && lines[0] === '') lines.shift();
  while (lines.length && lines[lines.length - 1] === '') lines.pop();
  if (!lines.length) return null;

  const title = lines[0];
  const rest = lines.slice(1);
  const blockKind = getBlockKind(title);
  return { title, nodes: parseBodyLines(rest, { blockKind, ctx }) };
}

function parseMajorTitle(title) {
  const m = String(title).match(/^(\d+)\.\s+(.*)$/);
  if (!m) return { number: null, label: title };
  return { number: m[1], label: m[2] };
}

function parseGuide(raw) {
  const ctx = buildParseContext(raw);
  const normalized = String(raw || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const parts = [];
  let current = [];
  for (const line of normalized.split('\n')) {
    if (SEPARATOR.test(line.trim())) {
      parts.push(current);
      current = [];
    } else {
      current.push(line);
    }
  }
  if (current.length) parts.push(current);

  const blocks = parts.map((lines) => parseBlock(lines.join('\n'), ctx)).filter(Boolean);

  let docTitle = 'Guide to HPS';
  const sections = [];
  let afterPhilosophy = false;

  for (let bi = 0; bi < blocks.length; bi += 1) {
    const block = blocks[bi];

    if (bi === 0) {
      docTitle = block.title;
      let welcomeNodes = [...(block.nodes || [])];
      let welcomeTitle = block.title;
      if (welcomeNodes[0]?.type === 'h3') {
        welcomeTitle = welcomeNodes[0].text;
        welcomeNodes = welcomeNodes.slice(1);
      }
      sections.push({ title: welcomeTitle, nodes: welcomeNodes, isWelcome: true });
      continue;
    }

    if (afterPhilosophy && !isMajorSectionTitle(block.title)) {
      const card = {
        title: block.title,
        paragraphs: (block.nodes || [])
          .filter((n) => n.type === 'p' || n.type === 'emphasis')
          .map((n) => n.text),
      };
      const prev = sections[sections.length - 1];
      if (prev && isPhilosophySectionTitle(prev.title)) {
        if (!prev.principles) prev.principles = [];
        prev.principles.push(card);
        continue;
      }
    }

    if (isPhilosophySectionTitle(block.title)) {
      afterPhilosophy = true;
      const nodes = [];
      const principles = [];
      const bl = block.nodes || [];
      let i = 0;
      while (i < bl.length) {
        const n = bl[i];
        if (n.type === 'h3') {
          const paras = [];
          i += 1;
          while (i < bl.length && (bl[i].type === 'p' || bl[i].type === 'emphasis')) {
            paras.push(bl[i].text);
            i += 1;
          }
          principles.push({ title: n.text, paragraphs: paras });
          continue;
        }
        nodes.push(n);
        i += 1;
      }
      sections.push({ title: block.title, nodes, principles });
      continue;
    }

    if (isMajorSectionTitle(block.title)) {
      afterPhilosophy = false;
    }

    if (isFinalNotesTitle(block.title)) {
      afterPhilosophy = false;
      const closing = ctx.closingLine;
      const nodes = (block.nodes || []).filter(
        (n) => !(closing && n.type === 'emphasis' && n.text === closing)
      );
      sections.push({ title: block.title, nodes, isFinal: true, closing });
      continue;
    }

    sections.push(block);
  }

  return { docTitle, sections };
}

function SectionHeading({ title }) {
  const { number, label } = parseMajorTitle(title);
  return (
    <SectionTitle>
      {number ? <NumberBadge>{number}</NumberBadge> : null}
      {label}
    </SectionTitle>
  );
}

function RenderNodes({ nodes }) {
  if (!nodes?.length) return null;

  // Group consecutive glance items into one responsive grid
  const chunks = [];
  let glanceBuf = [];
  const flushGlance = () => {
    if (!glanceBuf.length) return;
    chunks.push({ type: 'glance', cards: glanceBuf });
    glanceBuf = [];
  };
  for (const node of nodes) {
    if (node.type === 'glanceItem') {
      glanceBuf.push({ label: node.label, value: node.value });
    } else {
      flushGlance();
      chunks.push(node);
    }
  }
  flushGlance();

  return (
    <>
      {chunks.map((node, idx) => {
        if (node.type === 'p') return <Paragraph key={idx}>{node.text}</Paragraph>;
        if (node.type === 'emphasis') return <EmphasisLine key={idx}>{node.text}</EmphasisLine>;
        if (node.type === 'note') return <InfoNote key={idx}>{node.text}</InfoNote>;
        if (node.type === 'h3') return <SubsectionTitle key={idx}>{node.text}</SubsectionTitle>;
        if (node.type === 'ul') {
          return (
            <BulletList key={idx}>
              {node.items.map((item, j) => (
                <BulletItem key={j}>{item}</BulletItem>
              ))}
            </BulletList>
          );
        }
        if (node.type === 'ol') {
          return (
            <NumberList key={idx}>
              {node.items.map((item, j) => (
                <NumberItem key={j}>
                  <span className="num-badge">{item.n || j + 1}</span>
                  <div className="num-body">
                    {item.title ? <span className="num-title">{item.title}</span> : null}
                    {item.body ? <p className="num-text">{item.body}</p> : null}
                  </div>
                </NumberItem>
              ))}
            </NumberList>
          );
        }
        if (node.type === 'table') {
          const [header, ...body] = node.rows;
          return (
            <TableWrap key={idx}>
              <DataTable>
                {header ? (
                  <thead>
                    <tr>
                      {header.map((cell, c) => (
                        <th key={c}>{cell}</th>
                      ))}
                    </tr>
                  </thead>
                ) : null}
                <tbody>
                  {(header ? body : node.rows).map((row, r) => (
                    <tr key={r}>
                      {row.map((cell, c) => (
                        <td key={c}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </TableWrap>
          );
        }
        if (node.type === 'calendar') {
          return (
            <CalendarGrid key={idx}>
              {node.cards.map((card, c) => (
                <CalendarCard key={c}>
                  <h4>{card.label}</h4>
                  {card.dates[0] ? <p className="dates">{card.dates[0]}</p> : null}
                  {card.dates.slice(1).map((d, k) => (
                    <p className="extra" key={k}>
                      {d}
                    </p>
                  ))}
                </CalendarCard>
              ))}
            </CalendarGrid>
          );
        }
        if (node.type === 'glance') {
          return (
            <GlanceGrid key={idx}>
              {node.cards.map((card, c) => (
                <GlanceCard key={c}>
                  <span className="label">{card.label}</span>
                  <p className="value">{card.value}</p>
                </GlanceCard>
              ))}
            </GlanceGrid>
          );
        }
        return null;
      })}
    </>
  );
}

/**
 * @param {{ embedded?: boolean }} props
 */
export default function GuideToHpsPage({ embedded = false }) {
  const { i18n } = useTranslation();
  const guideLang = resolveGuideLang(i18n.language);
  const parsed = useMemo(
    () => parseGuide(GUIDE_TO_HPS_BY_LANG[guideLang]),
    [guideLang]
  );

  useSEO({
    title: `${parsed.docTitle} | Helvetic Padel Series`,
    description:
      'League guide and official rules for the Helvetic Padel Series — how the league operates and the competition rules for every team.',
    canonical: 'https://helveticpadelseries.ch/guide-to-hps',
  });

  return (
    <PageContainer $embedded={embedded}>
      <HeroSection>
        <HeroTitle>{parsed.docTitle}</HeroTitle>
      </HeroSection>

      <Container>
        {parsed.sections.map((section, i) => (
          <ContentSection key={`${section.title}-${i}`}>
            <SectionHeading title={section.title} />
            <RenderNodes nodes={section.nodes} />
            {section.principles?.length ? (
              <PrincipleGrid>
                {section.principles.map((p) => (
                  <PrincipleCard key={p.title}>
                    <h4>{p.title}</h4>
                    {p.paragraphs.map((text, j) => (
                      <p key={j}>{text}</p>
                    ))}
                  </PrincipleCard>
                ))}
              </PrincipleGrid>
            ) : null}
            {section.isFinal && section.closing ? (
              <ClosingBanner>{section.closing}</ClosingBanner>
            ) : null}
          </ContentSection>
        ))}
      </Container>
    </PageContainer>
  );
}
